import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useActivityCenterTracking } from '@xing-com/crate-activity-center-tracking';
import {
  useActivityCenterButtonRendered,
  useActivityCenterTotal,
  useIsActivityCenterVisible,
  useSetActivityCenterVisible,
} from '@xing-com/hub';
import { mediaTopBarVisible } from '@xing-com/layout-tokens';
import type { LinkProps } from '@xing-com/link';
import { Link } from '@xing-com/link';
import {
  cornerRadiusXL,
  scale010,
  scale020,
  scale030,
  scale040,
  spaceS,
  xdlColorText,
  xdlColorHover,
  xdlColorHoverSecondary,
  xdlColorFocusRing,
} from '@xing-com/tokens';
import { addParams } from '@xing-com/xing-tracking';

import { SuperEllipseWithTopBadge } from './super-ellipse-with-top-badge';

type ActivityCenterButtonProps = {
  title: string;
  href?: string;
  loginLink?: string;
};

export const ActivityCenterLoginButton: React.FC<ActivityCenterButtonProps> = ({
  title,
  href,
  loginLink,
}) => {
  useActivityCenterButtonRendered();

  const total = useActivityCenterTotal();
  const setActivityCenterVisible = useSetActivityCenterVisible();
  const isActivityCenterVisible = useIsActivityCenterVisible();
  const { trackInteraction } = useActivityCenterTracking();

  const justClosedRef = useRef<boolean>(false);
  const trackingURL =
    loginLink &&
    addParams(loginLink, {
      nwt_nav: 'activitycenter',
      signup_channel: 'minireg_activitycenter',
    });
  const navigateTo = total <= 0 && trackingURL ? trackingURL : href;

  useEffect(() => {
    if (isActivityCenterVisible) return;

    justClosedRef.current = true;

    const timerId = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timerId);
      justClosedRef.current = false;
    };
  }, [isActivityCenterVisible]);

  const onClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    if (total > 0) {
      event.preventDefault();

      if (!justClosedRef.current) {
        setActivityCenterVisible(!isActivityCenterVisible);
      }

      trackInteraction({
        event: 'clicked',
        element: 'activitycenter',
        elementDetail: 'activitycenter_open',
      });
    }
  };

  return (
    <StyledLink
      id="activity-center-button"
      data-testid="frame-vnav-activity-center-login-button"
      to={navigateTo}
      aria-label={title}
      onClick={onClick}
    >
      <SuperEllipseWithTopBadge count={total} />
      {title}
    </StyledLink>
  );
};

const StyledLink = styled(Link)<LinkProps>`
  color: ${xdlColorText};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  border-radius: ${cornerRadiusXL};
  padding: ${spaceS};

  @media ${mediaTopBarVisible} {
    border-radius: 0;
    padding: ${scale040} ${scale040} ${scale020};
    margin-bottom: ${scale030};
  }

  &:hover,
  &:active {
    text-decoration: none;
    background-color: ${xdlColorHoverSecondary};

    @media ${mediaTopBarVisible} {
      background-color: ${xdlColorHover};
    }

    @media (prefers-contrast: more) {
      outline: ${scale010} solid ${xdlColorFocusRing};
    }
  }

  &:focus {
    text-decoration: none;
    outline: ${scale010} solid ${xdlColorFocusRing};
  }
`;
